import React from "react";
import { COLOR } from "../constants/colors";

type BtnProps = {
  children?: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  type?: "button" | "submit" | "reset";
  ariaLabel?: string;
};

type PrimaryExtra = {
  selected?: boolean;
  validationError?: boolean;
};

const mergeClass = (base: string, extra?: string) =>
  `${base} ${extra ?? ""}`.trim();

export function PrimaryButton({
  children,
  onClick,
  disabled,
  className = "",
  style,
  type = "button",
  ariaLabel,
  // allow extra state-controlled props
  selected,
  validationError,
}: BtnProps & PrimaryExtra) {
  // base visuals (size/shape) — keep color logic below
  const defaultClass =
    "inline-flex items-center justify-center rounded-xl font-extrabold transition-all duration-200 hover:shadow-md";
  // compute colors based on selected / disabled
  const computedStyle: React.CSSProperties = disabled
    ? {
        backgroundColor: COLOR.border,
        color: COLOR.subtext,
        border: `2px solid ${COLOR.border}`,
      }
    : selected
    ? {
        backgroundColor: COLOR.main,
        color: COLOR.bg,
        border: `2px solid ${COLOR.mainBorder}`,
      }
    : {
        backgroundColor: COLOR.main, // default CTA color
        color: COLOR.bg,
        border: `2px solid ${COLOR.mainBorder}`,
      };

  if (validationError) {
    (computedStyle as any).animation = "shake 0.5s";
  }

  return (
    <button
      type={type}
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={disabled}
      className={mergeClass(defaultClass, className)}
      style={{ ...computedStyle, ...(style || {}) }}
    >
      {children}
    </button>
  );
}

export function BasicButton({
  children,
  onClick,
  disabled,
  className = "",
  style,
  type = "button",
  ariaLabel,
}: BtnProps) {
  const defaultClass =
    "inline-flex items-center justify-center rounded-md font-semibold";
  const defaultStyle: React.CSSProperties = {
    backgroundColor: COLOR.bg,
    color: COLOR.text,
    border: "none",
  };
  return (
    <button
      type={type}
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={disabled}
      className={`${defaultClass} ${className}`}
      style={{ ...defaultStyle, ...(style || {}) }}
    >
      {children}
    </button>
  );
}

export function TransparentButton({
  children,
  onClick,
  disabled,
  className = "",
  style,
  type = "button",
  ariaLabel,
}: BtnProps) {
  const defaultClass = "inline-flex items-center justify-center";
  const defaultStyle: React.CSSProperties = {
    background: "transparent",
    color: COLOR.text,
    border: "none",
  };
  return (
    <button
      type={type}
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={disabled}
      className={`${defaultClass} ${className}`}
      style={{ ...defaultStyle, ...(style || {}) }}
    >
      {children}
    </button>
  );
}

export function LinkButton({
  children,
  onClick,
  disabled,
  className = "",
  style,
  type = "button",
  ariaLabel,
}: BtnProps) {
  const defaultClass = "inline-flex items-center text-sm font-semibold";
  const defaultStyle: React.CSSProperties = {
    background: "transparent",
    color: COLOR.main,
    border: "none",
    padding: 0,
  };
  return (
    <button
      type={type}
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={disabled}
      className={`${defaultClass} ${className}`}
      style={{ ...defaultStyle, ...(style || {}) }}
    >
      {children}
    </button>
  );
}

export default {
  PrimaryButton,
  BasicButton,
  TransparentButton,
  LinkButton,
};
